import { ChangeEvent, useEffect, useRef, useState } from "react";
import styled from "styled-components/macro";
import { cssVariables } from "../../styles";
import { Tooltip } from "../tooltip/Tooltip";
import { Option } from "./Option";
interface Props<T> {
  id: string;
  placeholder: string;
  name: string;
  onSelect?: (value: T) => void;
  onClear?: () => void;
  getOptionList: (value: string) => Promise<T[]>;
  getSelectedOptionLabel: (value: T | null) => string;
  getListHtmlOptionLabel: (value: T | null) => string;
  value: T | null;
  wrapperHeightInRem: number;
  sidePaddingInRem: number;
  className?: string;
  validator?: () => void;
  disabled?: boolean;
}

export const Autocomplete = <T extends { id?: string | number }>({
  id,
  placeholder,
  onSelect,
  onClear,
  getOptionList,
  getSelectedOptionLabel,
  getListHtmlOptionLabel,
  value,
  wrapperHeightInRem,
  sidePaddingInRem,
  className,
  name,
  validator,
  disabled = false,
}: Props<T>) => {
  const [options, setOptions] = useState<T[]>([]);
  const [input, setInput] = useState("");
  const [preselected, setPreselected] = useState<T | null>(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const [mouseX, setMouseX] = useState<number | null>(null);
  const [mouseY, setMouseY] = useState<number | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setOptions([]);
  }, [value]);

  useEffect(() => {
    setPreselected(null);
  }, [input]);

  const updateInputList = async (value: string) => {
    let options: T[] = [];
    if (value) {
      options = await getOptionList(value);
    }
    setOptions(options);
  };

  const onClick = (value: T) => {
    if (value) {
      setInput(getSelectedOptionLabel(value));
      if (onSelect) {
        onSelect(value);
      }
      setOptions([]);
    }
  };

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    setInput(event.target.value);
    updateInputList(event.target.value);
  };

  const clearSelected = () => {
    setInput("");
    if (onClear) {
      onClear();
    }
    setOptions([]);
    setShowTooltip(false);
  };

  const handleonBlur = () => {
    setOptions([]);
    if (validator) {
      validator();
    }
  };

  const calculateShowTooltip = () => {
    if (inputRef.current?.offsetWidth && inputRef.current?.scrollWidth)
      return inputRef.current?.offsetWidth < inputRef.current?.scrollWidth;
    return false;
  };

  const previousValue = useRef(value);

  useEffect(() => {
    if (previousValue.current !== value) {
      if (validator) {
        validator();
      }
      previousValue.current = value;
    }
  }, [value, validator]);

  const handleButtonNavigation = (
    event: React.KeyboardEvent<HTMLDivElement>
  ) => {
    if (event.key === "ArrowUp" && options.length > 0) {
      if (!preselected) {
        setPreselected(options[options.length - 1]);
      }
      if (preselected) {
        options.forEach((option, index) => {
          if (option.id === preselected.id) {
            setPreselected(options[index - 1]);
          }
        });
      }
    }
    if (event.key === "ArrowDown" && options.length > 0) {
      if (!preselected) {
        setPreselected(options[0]);
      }
      if (preselected) {
        options.forEach((option, index) => {
          if (option.id === preselected.id) {
            setPreselected(options[index + 1]);
          }
        });
      }
    }
    if (event.key === "Enter" && preselected) {
      event.preventDefault();
      onClick(preselected);
    }
  };

  return (
    <AutocompleteWrapper className={className}>
      {options.length ? (
        <List
          numberOfOptions={options.length}
          //offSetInRem={wrapperHeightInRem}
          offSetInRem={6}
          width="inherit"
          backgroundColor="inherit"
          sidePaddingInRem={sidePaddingInRem}
          onMouseEnter={() => setPreselected(null)}>
          {options.map(option => (
            <Option
              key={option.id}
              option={option}
              preselected={preselected}
              getListHtmlOptionLabel={getListHtmlOptionLabel}
              onClick={onClick}
            />
          ))}
        </List>
      ) : null}
      <InputWrapper
        onKeyDown={handleButtonNavigation}
        showTooltip={showTooltip}>
        <Tooltip left={mouseX || 0} top={mouseY || 0}>
          {getSelectedOptionLabel(value)}
        </Tooltip>
        <StyledInput
          ref={inputRef}
          className="autocomplete-input"
          autoComplete="off"
          disabled={disabled}
          id={id}
          onMouseOver={() => {
            setShowTooltip(calculateShowTooltip());
          }}
          onMouseMove={event => {
            // set +10 to make tooltip be a bit away from the cursor and prevent cursor going on top of tooltip
            setMouseX(event.clientX + 10);
            setMouseY(event.clientY + 10);
          }}
          name={name}
          placeholder={placeholder}
          onChange={onChange}
          onBlur={handleonBlur}
          value={input ? input : getSelectedOptionLabel(value)}
          onKeyDown={(event: React.KeyboardEvent) => {
            if (value && !(event.code === "Tab")) {
              clearSelected();
            }
          }}
        />
      </InputWrapper>
    </AutocompleteWrapper>
  );
};

const AutocompleteWrapper = styled.div`
  display: flex;
  width: inherit;
`;

const InputWrapper = styled.div<{ showTooltip: boolean }>`
  display: flex;
  width: inherit;
  &:hover span {
    display: ${props => (props.showTooltip ? "block" : "none")};
  }
`;

const StyledInput = styled.input`
  text-overflow: ellipsis;
  width: inherit;
  padding: 0;
  font-size: 1.4rem;
  font-weight: 400;
  border: 0;
  outline: 0;
  background-color: ${cssVariables.colors.white};
  color: ${cssVariables.colors.eyOffBlack};
  ::placeholder {
    /* Chrome, Firefox, Opera, Safari 10.1+ */
    color: ${cssVariables.colors.eyGray};
  }
`;

const List = styled.div<{
  numberOfOptions: number;
  offSetInRem: number;
  sidePaddingInRem: number;
  backgroundColor?: string;
  color?: string;
  width?: string;
}>`
  position: absolute;
  z-index: 4;
  // transform from the centre of the box, by height of box / 2
  box-sizing: border-box;
  margin-left: ${props => {
    return "calc(-" + props.sidePaddingInRem + "rem)";
  }};
  margin-top: ${props => {
    return "calc(" + props.offSetInRem / 2 + "rem - 0.1rem)";
  }};
  background-color: ${props => {
    return props.backgroundColor;
  }};

  color: ${props => {
    return props.color;
  }};

  width: ${props => {
    return props.width;
  }};

  font-size: 1.5rem;
  font-weight: 300;
  letter-spacing: 0;
  line-height: 1.5rem;
  border: 1px solid ${cssVariables.colors.eyGray2};
  overflow: hidden;
`;

List.defaultProps = {
  backgroundColor: "inherit",
  color: "inherit",
  width: "inherit",
};
